import Link from 'next/link';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';

import { Alert, AlertDescription } from '@kit/ui/alert';
import { Trans } from '@kit/ui/trans';

/**
 * @name AuthErrorAlert
 * @param error This error comes from Supabase as the code returned on errors
 * This error is mapped from the translation auth:errors.{error}
 * To update the error messages, please update the translation file
 * https://github.com/supabase/gotrue-js/blob/master/src/lib/errors.ts
 * @constructor
 */
export function AuthErrorAlert({
  error,
}: {
  error: Error | null | undefined | string;
}) {
  if (!error) {
    return null;
  }

  const errorCode = error instanceof Error ? error.message : error;

  // Always include link components for error messages that need them
  const linkComponents = {
    SignInLink: (
      <Link href="/auth/sign-in" className="underline hover:text-foreground" />
    ),
    ResetPasswordLink: (
      <Link href="/auth/password-reset" className="underline hover:text-foreground" />
    ),
  };

  const DefaultError = (
    <Trans i18nKey="auth:errors.default" components={linkComponents} />
  );

  const components: Record<string, React.ReactElement> = {
    DefaultError,
    ...linkComponents,
  };

  return (
    <Alert variant={'destructive'}>
      <ExclamationTriangleIcon className={'w-4'} />

      <AlertDescription data-test={'auth-error-message'}>
        <Trans
          i18nKey={`auth:errors.${errorCode}`}
          defaults={'<DefaultError />'}
          components={components}
        />
      </AlertDescription>
    </Alert>
  );
}
